import { Component, Inject, NgZone } from '@angular/core';
import { timer, Subscription, BehaviorSubject } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Device } from '../device';
import { ActivatedRoute, Router } from '@angular/router';
import { JWTTokenService } from '../app.jwt-token.service';
import { AppService } from '../app.service';
import { CryptoService } from '../crypto.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { FreeTierDialog } from '../usage/free-tier-dialog.component';
import { PlanGateService } from '../services/plan-gate.service';
import { SITE_LINKS } from '../site-links';
import { CHATGPT_APP_URL, CLAUDE_CONNECTOR_URL, MCP_URL, MUSE_PASTE_BLOCK, MUSE_URL } from '../connect-apps/connect-apps-dock.component';

declare global {
  interface Window {
    google: any;
    AppleID: any;
  }
}

interface WizStep {
  title: string;
  body: string;
  img: string;
  showLaunch?: boolean;
  showCopy?: boolean;
  /** A whole message to paste into a chat, for apps that are set up by talking
   *  to them rather than by filling in a connector form. */
  pasteBlock?: string;
  reassure?: string;
}

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})

export class LoginComponent {
  readonly links = SITE_LINKS;
  device: any
  emailId: any
  otp: any
  dev: Device | any;
  private _errorMsg = new BehaviorSubject("");
  otpNav: boolean = false;
  pl: any;
  loginForm!: FormGroup;
  returnUrl: string = '/workspace';
  interactionInProgress = false;
  googleClientId: string = '';

  countDown: number = 30;
  resendEnabled: boolean = false;
  private timerSubscription?: Subscription;
  showApiTools: boolean = false;
  activeDemoTab: 'claude' | 'perplexity' | 'askbuddy' = 'claude';

  // Connector setup guide (Tutorial section)
  activeConnectorTab: 'claude' | 'chatgpt' = 'claude';
  mcpUrl: string = MCP_URL;

  // LIAM's public listing in the ChatGPT app directory (live 2026-08-20).
  // Replaces the old Developer-mode custom-connector flow: users now install
  // from the directory, so they never paste the MCP URL or toggle dev mode.
  chatgptAppUrl: string = CHATGPT_APP_URL;
  mcpCopied: boolean = false;

  // ============================================
  // Guided setup wizard (non-technical friendly)
  // ============================================
  wizardPlatform: 'claude' | 'chatgpt' | 'muse' | 'openclaw' | null = null;
  wizardIndex: number = 0;
  wizardDone: boolean = false;
  copiedPrompt: number = -1;
  copiedCmd: number = -1;

  // OpenClaw is a CLI / developer flow — no screenshots, just commands + the GitHub guide.
  // Users need BOTH the setup guide (OPENCLAW.md) and the SKILL.md file.
  openclawGuideUrl: string = 'https://github.com/netsys-usa/liam-agent/blob/master/skills/openclaw/OPENCLAW.md';
  openclawSkillUrl: string = 'https://github.com/netsys-usa/liam-agent/blob/master/skills/openclaw/SKILL.md';
  openclawSteps = [
    { text: 'Install the mcporter tool (on your machine, and your VM if using cloud).', code: 'npm install -g mcporter' },
    { text: 'Sign in to LIAM. This opens your browser — sign in, then stop the process. Your login is saved automatically.', code: 'npx mcporter auth https://mcp.askliam.ai/mcp' },
    { text: 'Check it works — you should get a JSON response back.', code: 'mcporter call \'https://mcp.askliam.ai/mcp.ListMemory(query: "", tokens: [])\'' },
    { text: 'Add the skill: create a skills folder and copy SKILL.md from the repo into it. Then turn off OpenClaw’s built-in memory.', code: 'openclaw config set plugins.slots.memory none' },
    { text: 'Restart the gateway, then type “check memory” in the OpenClaw chat to test.', code: 'openclaw gateway restart' }
  ];

  private claudeWizard: WizStep[] = [
    {
      // claude-2.png used to sit here - the old dialog, with Advanced settings
      // and an OAuth client id on the first screen. Claude has since split
      // that across two screens, so the shot below is the connectors page
      // instead: it is what you land on if the dialog does not open by itself.
      title: 'Open Claude',
      body: 'Tap the button below. It opens the box where you add LIAM. If Claude asks you to log in, do that first — then tap it again. Landed on the connectors list instead? Tap <b>Add</b>, then <b>Add custom connector</b>.',
      img: 'assets/images/tutorial/claude-1.png',
      showLaunch: true
    },
    {
      title: 'Name it and paste your link',
      body: 'Give the connector a name and paste your link below it, then tap <b>Continue</b>. Both values are in the <b>Your apps</b> bar at the bottom of this page, ready to copy.',
      img: 'assets/images/tutorial/claude-connector-1-details.png',
      showCopy: true
    },
    {
      // Claude added this second screen to the dialog - the old flow went
      // straight from the URL field to Add, and skipping past it silently
      // left people staring at radio buttons the steps never mentioned.
      title: 'Leave the sign-in options alone',
      body: 'Claude reads the right settings off LIAM and marks them <b>Detected</b>. Change nothing here — just tap <b>Add</b>.',
      img: 'assets/images/tutorial/claude-connector-2-auth.png'
    },
    {
      title: 'Tap Connect',
      body: 'LIAM now shows up in your connectors. Tap the <b>Connect</b> button.',
      img: 'assets/images/tutorial/claude-connector-3-connect.webp'
    },
    {
      title: 'Sign in to LIAM',
      body: 'Tap <b>Continue with Google</b> to sign in.',
      img: 'assets/images/tutorial/claude-5.png',
      reassure: 'This is LIAM’s own secure sign-in. It only checks that it’s really you — nothing else.'
    },
    {
      title: 'You’re basically done',
      body: 'You’ll see a list of things LIAM can do. Just leave them as they are.',
      img: 'assets/images/tutorial/claude-6.png'
    },
    {
      title: 'Use it in any chat',
      body: 'Open any chat, tap the <b>+</b> button, choose <b>Connectors</b>, and switch on <b>LIAM Memory</b>. That’s it!',
      img: 'assets/images/tutorial/claude-7.png'
    }
  ];

  // LIAM is listed in the ChatGPT app directory, so this is now a four-tap
  // install. The old seven-step flow (Settings -> Developer mode -> New
  // connector -> paste MCP URL -> OAuth) described a private dev-mode
  // connector and no longer matches what users see.
  // LIAM is listed in the ChatGPT app directory, so this is now a four-tap
  // install. The old seven-step flow (Settings -> Developer mode -> New
  // connector -> paste MCP URL -> OAuth) described a private dev-mode
  // connector and no longer matches what users see.
  private gptWizard: WizStep[] = [
    {
      title: 'Open LIAM in ChatGPT',
      body: 'Tap the button below \u2014 it opens LIAM in ChatGPT. Log in to ChatGPT first if it asks. You can also find it any time under <b>Plugins</b> by searching \u201cLIAM\u201d.',
      img: 'assets/images/tutorial/gpt-live-1.png',
      showLaunch: true
    },
    {
      title: 'Tap Try in chat',
      body: 'That opens a new chat with <b>LIAM</b> already attached and a starter prompt filled in. Tap send to try it.',
      img: 'assets/images/tutorial/gpt-live-2.png'
    },
    {
      title: 'Sign in to LIAM',
      body: 'The first time you use it, LIAM asks you to sign in. Tap <b>Continue with Google</b>.',
      img: 'assets/images/tutorial/gpt-6.png',
      reassure: 'This is LIAM\u2019s own secure sign-in. It only checks that it\u2019s really you.'
    },
    {
      title: 'Use it in any chat',
      body: 'That\u2019s it. LIAM now lives under <b>Plugins</b> \u2014 in any chat tap <b>+</b> and pick it, or just type <b>&#64;LIAM</b>.',
      img: 'assets/images/tutorial/gpt-live-3.png'
    }
  ];

  /**
   * Muse has no connector form: you tell it what you want to connect and it
   * works out the OAuth handshake itself. So the thing to copy here is a
   * message, and the steps are screenshots of a conversation rather than of a
   * settings pane.
   */
  private museWizard: WizStep[] = [
    {
      title: 'Ask Muse to connect it',
      body: 'Open Muse and send it the message below — it has everything Muse needs to set LIAM up.',
      img: 'assets/images/tutorial/muse-1-ask.png',
      showLaunch: true,
      pasteBlock: MUSE_PASTE_BLOCK
    },
    {
      title: 'Tap Connect on the card',
      body: 'Muse reads LIAM’s sign-in setup and drops a <b>LIAM</b> card into the chat. Tap <b>Connect</b> on it.',
      img: 'assets/images/tutorial/muse-2-connect.png'
    },
    {
      title: 'Confirm the connector',
      body: 'A box explains this is a custom connector and that you can disconnect any time. Read it, then tap <b>Connect</b>.',
      img: 'assets/images/tutorial/muse-3-allow.png'
    },
    {
      title: 'Sign in to LIAM',
      body: 'LIAM’s own sign-in opens. Tap <b>Continue with Google</b> and approve the read and write access it asks for.',
      img: 'assets/images/tutorial/muse-4-signin.webp',
      reassure: 'This is LIAM’s own secure sign-in. It only checks that it’s really you — Muse never sees your password.'
    },
    {
      title: 'You’re done',
      body: 'Muse confirms LIAM is connected. Ask it to remember something and it lands in the same memory as your other apps.',
      img: 'assets/images/tutorial/muse-5-done.png'
    }
  ];

  examplePrompts = [
    { icon: '💡', text: 'Remember: launch the newsletter on Monday.' },
    { icon: '🥗', text: 'Log my lunch — a chicken caesar salad.' },
    { icon: '👥', text: 'Add Sarah Chen from Acme as a contact.' }
  ];

  // ============================================
  // Animated "Examples" — you + LIAM walk through the day
  // ============================================
  // Act 1 — capture (walking through the day)
  walkSteps = [
    { left: '20%', say: 'Hey LIAM, remember <b>Sarah from Acme</b>.', chip: 'Sarah Chen · Acme', key: 'contact' },
    { left: '42%', say: 'Log my lunch — <b>caesar salad</b>.', chip: 'Caesar salad · lunch', key: 'food' },
    { left: '64%', say: 'Save this receipt — <b>groceries, $42.10</b>.', chip: 'Groceries · $42.10', key: 'receipt' },
    { left: '86%', say: 'Remember — <b>newsletter Monday</b>.', chip: 'Newsletter · Mon', key: 'idea' }
  ];
  // Act 2 — recall (evening at home)
  recallSteps = [
    { ask: 'What did I spend at the grocery?', answer: '$42.10 — apples, coffee, oat milk.', key: 'receipt' },
    { ask: 'Who did I meet today?', answer: 'Sarah Chen, from Acme.', key: 'contact' },
    { ask: 'What did I have for lunch?', answer: 'A caesar salad.', key: 'food' }
  ];

  walkPairLeft = '10%';
  walkWalking = false;
  walkNoTrans = false;
  walkBubble = '';
  walkBubbleShow = false;
  walkAnswer = '';
  walkAnswerShow = false;
  walkNote = '';
  walkNoteShow = false;
  walkHome = false;
  walkActive = -1;
  walkChips: { text: string; key: string }[] = [];
  walkChipGlow = '';
  liamCatch = false;
  flyText = '';
  flyKey = '';
  flyShow = false;

  private walkRunning = false;
  private walkTimers: any[] = [];
  private walkWait(ms: number) {
    return new Promise<void>(r => this.walkTimers.push(setTimeout(r, ms)));
  }

  private async startWalkLoop() {
    if (this.walkRunning) return;
    this.walkRunning = true;
    while (this.walkRunning) {
      // ---- Act 1: capture ----
      for (let i = 0; i < this.walkSteps.length; i++) {
        const s = this.walkSteps[i];
        this.walkBubbleShow = false;
        this.walkWalking = true;
        this.walkPairLeft = s.left;
        await this.walkWait(2000);
        if (!this.walkRunning) return;
        this.walkWalking = false;
        this.walkActive = i;
        await this.walkWait(250);
        this.walkBubble = s.say;
        this.walkBubbleShow = true;
        await this.walkWait(800);
        this.liamCatch = true;
        this.flyText = s.chip;
        this.flyKey = s.key;
        this.flyShow = true;
        await this.walkWait(1800);
      }
      this.walkBubbleShow = false;
      this.walkActive = -1;

      // ---- Transition: later that evening ----
      this.walkNote = 'Later that evening…';
      this.walkNoteShow = true;
      this.walkHome = true;
      this.walkWalking = true;
      this.walkPairLeft = '50%';
      await this.walkWait(2000);
      if (!this.walkRunning) return;
      this.walkWalking = false;
      await this.walkWait(500);
      this.walkNoteShow = false;

      // ---- Act 2: recall ----
      for (const r of this.recallSteps) {
        this.walkAnswerShow = false;
        this.walkBubble = r.ask;
        this.walkBubbleShow = true;
        this.walkChipGlow = r.key;
        await this.walkWait(1200);
        if (!this.walkRunning) return;
        this.walkAnswer = r.answer;
        this.walkAnswerShow = true;
        await this.walkWait(2300);
        this.walkBubbleShow = false;
        this.walkAnswerShow = false;
        this.walkChipGlow = '';
        await this.walkWait(350);
      }

      // ---- reset ----
      this.walkHome = false;
      this.walkChips = [];
      this.walkNoTrans = true;
      this.walkPairLeft = '10%';
      await this.walkWait(60);
      this.walkNoTrans = false;
      await this.walkWait(700);
    }
  }

  onFlyDone() {
    this.flyShow = false;
    this.walkChips.push({ text: this.flyText, key: this.flyKey });
  }

  private stopWalkLoop() {
    this.walkRunning = false;
    this.walkTimers.forEach(t => clearTimeout(t));
    this.walkTimers = [];
  }

  get wizardSteps(): WizStep[] {
    if (this.wizardPlatform === 'chatgpt') return this.gptWizard;
    if (this.wizardPlatform === 'muse') return this.museWizard;
    return this.claudeWizard;
  }
  get wizardTotal(): number {
    return this.wizardSteps.length;
  }
  get currentStep(): WizStep {
    return this.wizardSteps[this.wizardIndex];
  }
  get wizardProgress(): number {
    return Math.round(((this.wizardIndex + 1) / this.wizardTotal) * 100);
  }
  get platformLabel(): string {
    if (this.wizardPlatform === 'chatgpt') return 'ChatGPT';
    if (this.wizardPlatform === 'muse') return 'Muse';
    if (this.wizardPlatform === 'openclaw') return 'OpenClaw';
    return 'Claude';
  }
  get launchUrl(): string {
    if (this.wizardPlatform === 'chatgpt') return this.chatgptAppUrl;
    if (this.wizardPlatform === 'muse') return MUSE_URL;
    return CLAUDE_CONNECTOR_URL;
  }

  /** Height of the sticky page header, so a scrolled-to section is not tucked
   *  underneath it. */
  private readonly SCROLL_OFFSET = 70;

  /** Hero badge -> the ChatGPT setup, already selected. The badge announces
   *  the launch, so the obvious next thing to want is how to add it. */
  goToChatGptSetup(): void {
    this.startWizard('chatgpt');
    this.scrollToSection('tutorial');
  }

  startWizard(platform: 'claude' | 'chatgpt' | 'muse' | 'openclaw') {
    this.wizardPlatform = platform;
    this.wizardIndex = 0;
    this.wizardDone = false;
  }
  wizardReset() {
    this.wizardPlatform = null;
    this.wizardIndex = 0;
    this.wizardDone = false;
  }
  wizardBack() {
    if (this.wizardIndex > 0) this.wizardIndex--;
  }
  wizardNext() {
    if (this.wizardIndex < this.wizardTotal - 1) {
      this.wizardIndex++;
    } else {
      this.wizardDone = true;
    }
  }
  copyPrompt(i: number, text: string) {
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedPrompt = i;
      setTimeout(() => this.copiedPrompt = -1, 1500);
    });
  }

  copyCmd(i: number, text: string) {
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedCmd = i;
      setTimeout(() => this.copiedCmd = -1, 1500);
    });
  }

  // ============================================
  // OPTION 3: Auto-setup splash screen properties
  // ============================================
  isAutoSetupInProgress: boolean = false;
  autoSetupMessage: string = '';
  currentFeature: number = 0;
  setupProgress: number = 0;
  private featureInterval: any;
  private minimumDisplayTime: number = 18000; // 18 seconds minimum display
  private setupStartTime: number = 0;

  /**
   * Take someone straight to Stripe Checkout for the one paid plan.
   *
   * Used at the end of login when the allowance is gone or nearly gone: they
   * have already been told why, so the plans page would just be another click
   * on the same decision.
   *
   * Never dead-ends. If anything at all goes wrong - no purchasable tier, a
   * refused checkout, a network error - it falls back to /subscription, which
   * explains the situation and offers the button.
   */
  private async startUpgradeCheckout(apiKey: string, workspaceName: string): Promise<void> {
    const toPlans = () => this.router.navigate(['/subscription']);

    try {
      const payload: any = { routeType: "UI_SESSION" };
      const sign = async (body: any) => {
        const signed = await this.appService.wssign(body, workspaceName);
        if (!signed) throw new Error('Failed to sign request');
        return new TextDecoder().decode(signed);
      };

      const tiersResponse = await this.appService.wsPostRequestWithHeaders(
        payload, "usage/tiers", apiKey, await sign(payload)
      );

      const tiers: any[] = tiersResponse?.data?.tiers || [];
      // Cheapest sellable plan: same rule as the backend's upgrade link.
      const paid = tiers
        .filter(t => t.purchasable && t.name?.toLowerCase() !== 'free')
        .sort((a, b) => (a.monthly_token_limit || 0) - (b.monthly_token_limit || 0))[0];

      if (!paid) {
        console.log('No purchasable tier, showing the plans page instead');
        await toPlans();
        return;
      }

      const checkoutPayload: any = {
        routeType: "UI_SESSION",
        tier: paid.name,
        email: this.appService.get('xdbc.emailId'),
      };
      const checkoutResponse = await this.appService.wsPostRequestWithHeaders(
        checkoutPayload, "usage/initiate-checkout", apiKey, await sign(checkoutPayload)
      );

      const checkoutUrl = checkoutResponse?.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      console.log('Checkout not started, showing the plans page instead',
                  checkoutResponse?.data?.code || checkoutResponse?.message);
      await toPlans();
    } catch (error) {
      console.error('Could not start checkout, showing the plans page instead:', error);
      await toPlans();
    }
  }

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private jwtService: JWTTokenService,
    public appService: AppService,
    private cs: CryptoService,
    private fb: FormBuilder,
    private ngZone: NgZone,
    private planGate: PlanGateService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  get email() {
    return this.loginForm.get('email')!;
  }

  ngOnInit() {
    this.otpNav = false;
    this.loadGoogleConfig();
  }

  ngAfterViewInit() {
    this.initializeGoogleSignIn();
    setTimeout(() => this.startWalkLoop(), 600);

    // Support cross-page nav: links from other pages arrive as /login?scrollTo=<section>
    this.route.queryParams.subscribe(params => {
      const target = params['scrollTo'];
      if (target) {
        setTimeout(() => this.scrollToSection(target), 150);
      }
    });
  }

  async loadGoogleConfig() {
    try {
      const config = await this.appService.getRequest("auth/google-config") as any;

      if (config && config.status === "Success" && config.data?.clientId) {
        this.googleClientId = config.data.clientId;
        this.loadGoogleSignIn();
      } else {
        console.warn('Google login not configured on server');
      }
    } catch (error) {
      console.error('Failed to load Google config:', error);
    }
  }

  loadGoogleSignIn() {
    if (!document.getElementById('google-signin-script')) {
      const script = document.createElement('script');
      script.id = 'google-signin-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  initializeGoogleSignIn() {
    if (typeof window.google !== 'undefined') {
      window.google.accounts.id.initialize({
        client_id: '916010213357-ldjmqsejtcirq064afb19i5ds5slnt0k.apps.googleusercontent.com',
        callback: this.handleGoogleSignIn.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true
      });

      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          width: 180,
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left'
        }
      );
    } else {
      setTimeout(() => this.initializeGoogleSignIn(), 500);
    }
  }

  async handleGoogleSignIn(response: any) {
    this.ngZone.run(async () => {
      try {
        const obj = {
          credential: response.credential
        };

        const res = await this.appService.postRequest(obj, "auth/google-login");

        if (res && res.status === "Success") {
          this.appService.set('xdbc.emailId', res?.data?.email);
          this.appService.set('xdbc.accessToken', res?.data?.access_token);
          this.appService.set('xdbc.justLoggedIn', 'true');

          await this.loadWorkspaceAndCheckTier();
        } else {
          this.appService.addErrorMessage(res.message || 'Google sign-in failed');
        }
      } catch (error) {
        console.error('Google sign-in error:', error);
        this.appService.addErrorMessage('Failed to sign in with Google. Please try again.');
      }
    });
  }

  async onOtpChange(otp: any) {
    this.otp = otp;
  }

  async loginWithOtp() {
    try {
      let obj = {
        email: this.emailId,
        otp: this.otp,
      };

      const res = await this.appService.postRequest(obj, "auth/verify-email-otp");
      if (res && res.status === "Success") {
        this.appService.set('xdbc.emailId', res?.data?.email);
        this.appService.set('xdbc.accessToken', res?.data?.access_token);
        this.appService.set('xdbc.justLoggedIn', 'true');

        await this.loadWorkspaceAndCheckTier();
      } else {
        this.appService.addErrorMessage(res.message);
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.error?.code == "INVALID_OTP") {
        this.appService.addErrorMessage('Hmm, that code didn\'t work. Try again or request a new one.')
      }
    }
  }

  /**
   * Load workspace first, then check tier and show dialog
   */
  async loadWorkspaceAndCheckTier() {
    try {
      console.log('Step 1: Loading workspace...');
      const workspaceData = await this.loadWorkspace();

      if (!workspaceData) {
        console.log('No workspace data - First time user detected!');
        await this.autoSetupFirstTimeUser();
        return;
      }

      console.log('Workspace loaded:', workspaceData.workspaceName);
      console.log('Step 2: Checking tier...');
      await this.checkTierAndShowDialog();

    } catch (error) {
      console.error('Error in loadWorkspaceAndCheckTier:', error);
      this.router.navigate(['/workspace']);
    }
  }

  /**
   * Load workspace data from API
   */
  async loadWorkspace(): Promise<any> {
    try {
      const obj: any = {
        userId: 1,
        email: this.appService.get('xdbc.emailId')
      };

      const res = await this.appService.postRequest(obj, "auth/list-workspace");

      if (res && res.data && res.data.length > 0) {
        const workspaces = res.data;
        const defaultWorkspace = workspaces.find((ws: any) => ws.default);

        if (defaultWorkspace) {
          this.appService.set('xdbc.workspace', JSON.stringify(defaultWorkspace));
          this.appService.workspaceName = defaultWorkspace.workspaceName;
          return defaultWorkspace;
        } else if (workspaces.length > 0) {
          this.appService.set('xdbc.workspace', JSON.stringify(workspaces[0]));
          this.appService.workspaceName = workspaces[0].workspaceName;
          return workspaces[0];
        }
      }

      return null;
    } catch (error) {
      console.error('Error loading workspace:', error);
      return null;
    }
  }

  // ============================================
  // OPTION 3: AUTO-SETUP WITH SPLASH SCREEN
  // ============================================

  /**
   * Automatically sets up workspace, keys, and default user for first-time users
   * Ensures splash screen displays for at least 18 seconds
   */
  async autoSetupFirstTimeUser(): Promise<void> {
    const userEmail = this.appService.get('xdbc.emailId');

    if (!userEmail) {
      console.error('No email found for auto-setup');
      this.router.navigate(['/login']);
      return;
    }

    this.isAutoSetupInProgress = true;
    this.setupStartTime = Date.now();
    this.setupProgress = 0;

    // Start feature carousel
    this.startFeatureCarousel();

    try {
      // Step 1: Create default workspace (0-20%)
      this.autoSetupMessage = 'Creating your workspace...';
      this.animateProgress(20);
      console.log('Auto-setup Step 1: Creating workspace...');

      const workspaceResult = await this.createDefaultWorkspace(userEmail);

      if (!workspaceResult) {
        throw new Error('Failed to create workspace');
      }

      const { apiKey, workspaceName } = workspaceResult;
      console.log('Workspace created:', workspaceName);

      // Step 2: Generate and submit cryptographic keys (20-40%)
      this.autoSetupMessage = 'Setting up secure keys...';
      this.animateProgress(40);
      console.log('Auto-setup Step 2: Generating keys...');

      await this.generateAndSubmitKeys(apiKey, workspaceName);
      console.log('Keys generated and submitted');

      // Step 3: Activate workspace (40-60%)
      this.autoSetupMessage = 'Activating workspace...';
      this.animateProgress(60);
      console.log('Auto-setup Step 3: Activating workspace...');

      await this.activateWorkspace(apiKey, workspaceName);
      console.log('Workspace activated');

      // Step 4: Setup free tier (60-75%)
      this.autoSetupMessage = 'Setting up your account...';
      this.animateProgress(75);
      console.log('Auto-setup Step 4: Setting up free tier...');

      await this.setupFreeTier(apiKey, workspaceName, userEmail);
      console.log('Free tier setup complete');

      // Step 5: Create default user profile (75-90%)
      this.autoSetupMessage = 'Creating your profile...';
      this.animateProgress(90);
      console.log('Auto-setup Step 5: Creating user profile...');

      const userKey = await this.createDefaultUserProfile(apiKey, workspaceName, userEmail);
      console.log('User profile created with key:', userKey);

      // Step 6: Store workspace in localStorage
      const workspaceData = {
        apiKey: apiKey,
        workspaceName: workspaceName,
        default: true,
        status: 'ACTIVATED'
      };
      this.appService.set('xdbc.workspace', JSON.stringify(workspaceData));
      this.appService.workspaceName = workspaceName;

      // Step 7: Ensure minimum display time, then navigate
      this.autoSetupMessage = 'All set! Redirecting to chat...';
      this.animateProgress(100);
      console.log('Auto-setup complete!');

      // Calculate remaining time to meet minimum display
      const elapsedTime = Date.now() - this.setupStartTime;
      const remainingTime = Math.max(0, this.minimumDisplayTime - elapsedTime);

      console.log(`Elapsed: ${elapsedTime}ms, Waiting additional: ${remainingTime}ms`);

      // Wait for minimum display time
      await this.delay(remainingTime);

      // Cleanup and navigate
      this.stopFeatureCarousel();
      this.appService.remove('xdbc.justLoggedIn');
      this.appService.set('xdbc.showWelcomeMessage', 'true');
      this.appService.set('xdbc.isFirstTimeUser', 'true');

      this.isAutoSetupInProgress = false;

      // Navigate to chat with default user
      if (userKey) {
        this.router.navigate(['/chat/' + userKey]);
      } else {
        this.router.navigate(['/workspace']);
      }

      this.appService.addSuccessMessage('Welcome! Your workspace is ready.');

    } catch (error: any) {
      console.error('Auto-setup failed:', error);
      this.stopFeatureCarousel();
      this.isAutoSetupInProgress = false;
      this.appService.addErrorMessage(
        'Setup encountered an issue: ' + (error.message || 'Please try again')
      );

      this.appService.remove('xdbc.justLoggedIn');
      this.router.navigate(['/workspace']);
    }
  }

  /**
   * Start the feature carousel rotation
   */
  startFeatureCarousel(): void {
    this.currentFeature = 0;
    this.featureInterval = setInterval(() => {
      this.currentFeature = (this.currentFeature + 1) % 4; // 4 features
    }, 3500); // Change every 3.5 seconds
  }

  /**
   * Stop the feature carousel
   */
  stopFeatureCarousel(): void {
    if (this.featureInterval) {
      clearInterval(this.featureInterval);
      this.featureInterval = null;
    }
  }

  /**
   * Smoothly animate progress to target value
   */
  animateProgress(target: number): void {
    const step = () => {
      if (this.setupProgress < target) {
        this.setupProgress += 1;
        setTimeout(step, 50);
      }
    };
    step();
  }

  /**
   * Step 1: Create default workspace
   */
  async createDefaultWorkspace(email: string): Promise<{ apiKey: string; workspaceName: string } | null> {
    try {
      const workspaceName = this.generateWorkspaceName(email);

      const payload = {
        name: email,
        email: email,
        institutionId: 'DEFAULT',
        institutionName: 'Personal',
        routeType: 'UI_SESSION'
      };

      const response = await this.appService.postRequest(payload, 'auth/register');

      if (response?.status === 'Success' && response.data) {
        return {
          apiKey: response.data.apiKey,
          workspaceName: response.data.name || workspaceName
        };
      }

      throw new Error(response?.message || 'Workspace creation failed');
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  }

  /**
   * Step 2: Generate and submit cryptographic keys
   */
  async generateAndSubmitKeys(apiKey: string, workspaceName: string): Promise<void> {
    try {
      const keyPair = await this.appService.getKeyPair();
      const publicKeyPEM = keyPair[0];
      const privateKeyPEM = keyPair[1];

      const submitKeyPayload = {
        apiKey: apiKey,
        publicKey: this.appService.convertPEMToBase64(publicKeyPEM),
        routeType: 'UI_SESSION'
      };

      const submitKeyResponse = await this.appService.postRequest(
        submitKeyPayload,
        'auth/submit-key'
      );

      if (submitKeyResponse?.status !== 'Success') {
        throw new Error(submitKeyResponse?.message || 'Key submission failed');
      }

      await this.appService.storePrivateKeyInCryptoDB(privateKeyPEM, workspaceName, submitKeyResponse?.data?.keyId);

    } catch (error: any) {
      console.error('Error generating/submitting keys:', error);
      throw error;
    }
  }

  /**
   * Step 3: Activate workspace
   */
  async activateWorkspace(apiKey: string, workspaceName: string): Promise<void> {
    try {
      const activationPayload = {
        name: workspaceName,
        routeType: 'UI_SESSION'
      };

      const signatureData = await this.appService.wssign(activationPayload, workspaceName);

      if (!signatureData) {
        throw new Error('Failed to sign activation payload');
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        activationPayload,
        'auth/activate',
        apiKey,
        signature
      );

      if (response?.status !== 'Success') {
        throw new Error(response?.message || 'Workspace activation failed');
      }
    } catch (error: any) {
      console.error('Error activating workspace:', error);
      throw error;
    }
  }

  /**
   * Step 4: Setup free tier for the user
   */
  async setupFreeTier(apiKey: string, workspaceName: string, email: string): Promise<void> {
    try {
      const tierPayload = {
        email: email,
        tier: 'free',
        routeType: 'UI_SESSION'
      };

      const signatureData = await this.appService.wssign(tierPayload, workspaceName);

      if (!signatureData) {
        throw new Error('Failed to sign tier setup payload');
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        tierPayload,
        'usage/setup',
        apiKey,
        signature
      );

      if (response?.status !== 'Success') {
        console.warn('Free tier setup returned:', response?.message);
      }
    } catch (error: any) {
      console.warn('Error setting up free tier (non-critical):', error);
    }
  }

  /**
   * Step 5: Create default user profile
   */
  async createDefaultUserProfile(apiKey: string, workspaceName: string, email: string): Promise<string | null> {
    try {
      const profilePayload = {
        name: email,
        identification: email,
        identificationType: 'UNIQUE_ID',
        routeType: 'UI_SESSION'
      };

      const signatureData = await this.appService.wssign(profilePayload, workspaceName);

      if (!signatureData) {
        throw new Error('Failed to sign profile creation payload');
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        profilePayload,
        'auth/create-profile',
        apiKey,
        signature
      );

      if (response?.status === 'Success') {
        return response.data?.userkey || response.data?.profileKey || null;
      }

      throw new Error(response?.message || 'Profile creation failed');
    } catch (error: any) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  generateWorkspaceName(email: string): string {
    const namePart = email.split('@')[0];
    const sanitized = namePart.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    return `${sanitized}_workspace`;
  }

  extractNameFromEmail(email: string): string {
    const namePart = email.split('@')[0];
    const words = namePart.split(/[._-]/);
    const capitalized = words.map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
    return capitalized.join(' ');
  }

  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ============================================
  // EXISTING METHODS (unchanged)
  // ============================================

  async checkTierAndShowDialog() {
    try {
      const wsJson = this.appService.get("xdbc.workspace");

      if (!wsJson || wsJson === 'undefined' || wsJson.trim() === '') {
        console.log('No workspace in localStorage, navigating to workspace');
        this.appService.remove('xdbc.justLoggedIn');
        this.router.navigate(['/workspace']);
        return;
      }

      let apiKey, workspaceName;

      try {
        const ws = JSON.parse(wsJson);
        apiKey = ws.apiKey;
        workspaceName = ws.workspaceName;

        if (!apiKey || !workspaceName) {
          console.log('Missing apiKey or workspaceName, navigating to workspace');
          this.appService.remove('xdbc.justLoggedIn');
          this.router.navigate(['/workspace']);
          return;
        }
      } catch (err) {
        console.error('Invalid workspace JSON:', err);
        this.appService.remove('xdbc.justLoggedIn');
        this.router.navigate(['/workspace']);
        return;
      }

      console.log('Step 3: Getting tier from API...');

      const activationPayload: any = {
        routeType: "UI_SESSION"
      };

      const signatureData = await this.appService.wssign(
        activationPayload,
        workspaceName
      );

      if (!signatureData) {
        throw new Error("Failed to sign activation payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        activationPayload,
        "usage/connector-summary",
        apiKey,
        signature
      );

      if (response && response?.status === "Success") {
        const usage = response?.data;
        console.log('Tier received:', usage?.tier);

        this.planGate.applySummary(usage);

        // Out of allowance on any tier: straight to billing, which is the only
        // route that stays reachable from here.
        if (this.planGate.blocked) {
          console.log('Plan allowance exhausted, forcing upgrade');
          this.appService.remove('xdbc.justLoggedIn');
          await this.startUpgradeCheckout(apiKey, workspaceName);
          return;
        }

        // Near the limit (backend decides where that line is, via nearLimit):
        // go straight to the plans page. Someone arriving from a quota email
        // has already read the pitch - a dialog here would just be a second
        // click on the same decision.
        // Near the limit, or arriving from a quota email (#/login?upgrade=...):
        // go straight to Stripe rather than to a page whose only purpose is one
        // more click. The parameter lives inside the hash, so it is a plain
        // route param this component owns - nothing strips it.
        const askedToUpgrade = !!this.route.snapshot.queryParamMap.get('upgrade');
        if (usage?.nearLimit || askedToUpgrade) {
          console.log('Near the allowance limit, starting checkout');
          this.appService.remove('xdbc.justLoggedIn');
          await this.startUpgradeCheckout(apiKey, workspaceName);
          return;
        }

        if (usage?.tier && usage.tier.toLowerCase() === 'free') {
          console.log('User is on free tier, showing dialog');
          this.showFreeTierDialog(usage?.monthlyLimit);
        } else {
          console.log('User is on paid tier, navigating to workspace');
          this.router.navigate(['/workspace']);
        }
      } else {
        console.log('Could not get tier info, navigating to workspace');
        this.router.navigate(['/workspace']);
      }
    } catch (error) {
      console.error('Error checking tier:', error);
      this.router.navigate(['/workspace']);
    }
  }

  showFreeTierDialog(freeLimit?: number): void {
    const dialogRef = this.dialog.open(FreeTierDialog, {
      width: '500px',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
      data: { freeLimit }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'upgrade') {
        this.appService.remove('xdbc.justLoggedIn');
        this.router.navigate(['/subscription']);
      } else {
        this.router.navigate(['/workspace']);
      }
    });
  }

  async onSubmit() {
    this.startResendTimer();
    if (this.loginForm.valid) {
      try {
        let obj = {
          email: this.emailId,
        };
        const res = await this.appService.postRequest(obj, "auth/get-email-otp");
        this.otpNav = true;
      } catch (error) {
        console.log("auth failure", error);
      }
    }
  }

  startResendTimer() {
    this.countDown = 30;
    this.resendEnabled = false;

    this.timerSubscription = timer(0, 1000).subscribe(() => {
      if (this.countDown > 0) {
        this.countDown--;
      } else {
        this.resendEnabled = true;
        if (this.timerSubscription) {
          this.timerSubscription.unsubscribe();
        }
      }
    });
  }

  async onResend() {
    if (!this.resendEnabled) return;
    try {
      await this.onSubmit();
    } catch (error) {
      console.error('Failed to resend OTP:', error);
    }
  }

  private async handleLoginRedirection(res: any) {
    this.appService.takeawayFeatureRestriction = false;
    if (res?.data?.authenticated === false) {
      this.appService.showNav = false;
    } else {
      await this.router.navigate(['/workspace']);
      this.appService.showNav = true;
    }
    return true;
  }

  scrollToFeature(featureId: string) {
    const element = document.getElementById(`feature-${featureId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  openGuide(guideType: 'mcp' | 'chrome' | 'devspace' | 'tools' | 'perplexity') {
    const routes: { [key: string]: string } = {
      'mcp': '/mcp-guide',
      'chrome': '/chrome-guide',
      'devspace': '/devspace-guide',
      'tools': '/tools-guide',
      'perplexity': '/perplexity-guide'
    };

    const route = routes[guideType];
    if (route) {
      this.router.navigate([route]);
    }
  }

  toggleApiTools() {
    this.showApiTools = !this.showApiTools;

    if (this.showApiTools) {
      setTimeout(() => {
        const element = document.querySelector('.api-content');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }

  switchDemoTab(tab: 'claude' | 'perplexity' | 'askbuddy') {
    this.activeDemoTab = tab;
  }

  switchConnectorTab(tab: 'claude' | 'chatgpt') {
    this.activeConnectorTab = tab;
  }

  blockCopied = false;

  /** Copies a whole chat message - name, link and auth type - in one go. */
  copyPasteBlock(text: string) {
    navigator.clipboard?.writeText(text).catch(() => { });
    this.blockCopied = true;
    setTimeout(() => (this.blockCopied = false), 2000);
  }

  copyMcpUrl() {
    navigator.clipboard?.writeText(this.mcpUrl).then(() => {
      this.mcpCopied = true;
      setTimeout(() => this.mcpCopied = false, 2000);
    });
  }

  // Falls back to the captioned placeholder when a step screenshot isn't present yet
  onShotError(ev: Event) {
    const el = (ev.target as HTMLElement)?.closest('.step-shot');
    el?.classList.add('no-img');
  }

  /** Angular reuses one <img> across wizard steps, so a step whose screenshot
   *  is missing left `no-img` stuck on the container and every later step
   *  rendered the placeholder over a picture that had loaded fine. */
  onShotLoad(ev: Event) {
    const el = (ev.target as HTMLElement)?.closest('.step-shot');
    el?.classList.remove('no-img');
  }

  /** Hand-rolled smooth scroll: the native one is unavailable on this
   *  container (see scrollToSection), and an instant jump across a 20,000px
   *  page is disorienting. */
  private animateScroll(scroller: HTMLElement, top: number): void {
    const start = scroller.scrollTop;
    const distance = top - start;
    if (Math.abs(distance) < 2) return;

    // requestAnimationFrame is suspended while the page is hidden, which would
    // leave the scroll half-done (or not started) on a background tab.
    if (document.hidden) {
      scroller.scrollTop = top;
      return;
    }

    const duration = 500;
    let startedAt: number | null = null;

    const step = (now: number) => {
      if (startedAt === null) startedAt = now;
      const t = Math.min(1, (now - startedAt) / duration);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      scroller.scrollTop = start + distance * eased;
      if (t < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);

    if (element) {
      this.ngZone.runOutsideAngular(() => {
        // This page scrolls inside <mat-sidenav-content>, not the window, and
        // that container reports clientHeight 0 - which makes the browser
        // treat every element as already visible. Both scrollIntoView() and
        // scrollTo({behavior:'smooth'}) therefore do nothing on it, so every
        // section link (nav items, ?scrollTo=, feature cards) changed the URL
        // and then sat still. Assigning scrollTop is the one thing that works.
        const scroller = element.closest('.mat-sidenav-content') as HTMLElement | null;

        if (scroller) {
          const top = element.getBoundingClientRect().top + scroller.scrollTop - this.SCROLL_OFFSET;
          this.animateScroll(scroller, Math.max(0, top));
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      if (sectionId === 'api' && !this.showApiTools) {
        setTimeout(() => {
          this.showApiTools = true;
        }, 600);
      }
    }
  }

  expandApiOnNav(): void {
    if (!this.showApiTools) {
      setTimeout(() => {
        this.showApiTools = true;
      }, 600);
    }
  }

  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  ngOnDestroy() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.stopFeatureCarousel();
    this.stopWalkLoop();
  }
}